import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { evaluationApi, templateApi } from "../performanceApi";
import useInvalidatingMutation from "./useInvalidatingMutation";

export const performanceKeys = {
  templates: (filters) => ["perfTemplates", filters],
  template: (id) => ["perfTemplate", id],
  evaluations: (filters) => ["perfEvaluations", filters],
  evaluationCounts: (filters) => ["perfEvaluationCounts", filters],
  evaluation: (id) => ["perfEvaluation", id],
  suggest: (employeeId) => ["perfSuggest", employeeId],
};

const TEMPLATE_KEYS = [["perfTemplates"], ["perfTemplate"]];

export const useTemplates = (filters = {}) =>
  useQuery({
    queryKey: performanceKeys.templates(filters),
    queryFn: () => templateApi.getAll(filters),
  });

export const useTemplate = (id) =>
  useQuery({
    queryKey: performanceKeys.template(id),
    queryFn: () => templateApi.getById(id),
    enabled: !!id,
    retry: false,
  });

// 409 = changed by someone else / wrong state: refetch so the screen shows the truth
const useTemplateMutation = (fn) => {
  const qc = useQueryClient();
  return useInvalidatingMutation(fn, TEMPLATE_KEYS, {
    onError: (error) => {
      if (error?.response?.status === 409)
        TEMPLATE_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
    },
  });
};

export const useCreateTemplate = () =>
  useTemplateMutation((body) => templateApi.create(body));
export const useUpdateTemplate = () =>
  useTemplateMutation(({ id, body }) => templateApi.update(id, body));
export const useNewTemplateVersion = () =>
  useTemplateMutation((id) => templateApi.newVersion(id));
export const useActivateTemplate = () =>
  useTemplateMutation((id) => templateApi.activate(id));
export const useArchiveTemplate = () =>
  useTemplateMutation((id) => templateApi.archive(id));
export const useDeleteTemplate = () =>
  useTemplateMutation((id) => templateApi.remove(id));

/* ---------- Evaluations ---------- */

// Everything an evaluation action can change
const EVALUATION_KEYS = [
  ["perfEvaluation"],
  ["perfEvaluations"],
  ["perfEvaluationCounts"],
  ["dashboardMe"],
];

export const useEvaluations = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: performanceKeys.evaluations(filters),
    queryFn: () => evaluationApi.getList(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

/** One call for every tab badge; pass the list filters without status/page/limit. */
export const useEvaluationCounts = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: performanceKeys.evaluationCounts(filters),
    queryFn: () => evaluationApi.getCounts(filters),
    enabled,
  });

export const useEvaluation = (id) =>
  useQuery({
    queryKey: performanceKeys.evaluation(id),
    queryFn: () => evaluationApi.getById(id),
    enabled: !!id,
    retry: false,
  });

export const useSuggestedTemplates = (employeeId) =>
  useQuery({
    queryKey: performanceKeys.suggest(employeeId),
    queryFn: () => evaluationApi.suggestTemplates(employeeId),
    enabled: !!employeeId,
    retry: false,
  });

// 409 = changed by someone else: refetch the evaluation and the lists
const refetchEvaluationsOn409 = (qc) => (error) => {
  if (error?.response?.status === 409)
    EVALUATION_KEYS.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
};

const useEvaluationMutation = (fn) => {
  const qc = useQueryClient();
  return useInvalidatingMutation(fn, EVALUATION_KEYS, {
    onError: refetchEvaluationsOn409(qc),
  });
};

export const useCreateEvaluation = () =>
  useEvaluationMutation((body) => evaluationApi.create(body));
export const useChangeEvaluator = () =>
  useEvaluationMutation(({ id, evaluatorEmployeeId }) =>
    evaluationApi.changeEvaluator(id, evaluatorEmployeeId),
  );
export const useCancelEvaluation = () =>
  useEvaluationMutation(({ id, reason }) => evaluationApi.cancel(id, reason));
export const useSubmitEvaluation = () =>
  useEvaluationMutation((id) => evaluationApi.submit(id));

export const useResolveContest = () =>
  useEvaluationMutation(({ id, action, remarks }) =>
    evaluationApi.resolveContest(id, { action, remarks }),
  );

export const useEvaluationReport = (filters) =>
  useQuery({
    queryKey: ["perfReport", filters],
    queryFn: () => evaluationApi.getReportSummary(filters),
    placeholderData: keepPreviousData,
  });

/** Save draft ratings: the response (with fresh scores) replaces the cached details, no refetch. */
export const useSaveEvaluationResponses = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, responses }) =>
      evaluationApi.saveResponses(id, responses),
    onSuccess: (details, { id }) =>
      qc.setQueryData(performanceKeys.evaluation(id), details),
    onError: refetchEvaluationsOn409(qc),
  });
};
